import type { TrainingDocument } from '~/gql/documents/documents'
import { Archive, Download, Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import { formatBytes } from '~/utils/bytes'

type IProps = {
  documents: readonly TrainingDocument[]
  onDownload: (document: TrainingDocument) => void
  onEdit: (document: TrainingDocument) => void
  onAcknowledgements: (document: TrainingDocument) => void
  onArchive: (document: TrainingDocument) => void
}

/** Une ligne par document (#326) : pour quel parcours, à accepter ou non, combien l'ont lu. */
function DocumentsTable({ documents, onDownload, onEdit, onAcknowledgements, onArchive }: IProps) {
  const { t, i18n } = useTranslation()
  const action = (label: string, document: TrainingDocument) => t('common.actionFor', { action: label, name: document.title })

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('documents.column.title')}</TableHead>
          <TableHead>{t('documents.column.session')}</TableHead>
          <TableHead>{t('documents.column.required')}</TableHead>
          <TableHead>{t('documents.column.acks')}</TableHead>
          <TableHead><span className="sr-only">{t('documents.column.actions')}</span></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.map(d => (
          <TableRow key={d.id}>
            <TableCell>
              <div className="flex min-w-0 flex-col">
                <span className="font-medium">{d.title}</span>
                <span className="text-xs text-muted-foreground">{`${d.fileName} · ${formatBytes(d.sizeBytes, i18n.language)}`}</span>
              </div>
            </TableCell>
            <TableCell>{d.sessionName ?? t('documents.allSessions')}</TableCell>
            <TableCell>{d.requiredBeforeStart ? <Badge variant="warningSubtle">{t('documents.requiredBadge')}</Badge> : t('documents.optional')}</TableCell>
            <TableCell>
              <Button variant="link" className="h-auto p-0" onClick={() => onAcknowledgements(d)} aria-label={action(t('documents.acks.open'), d)}>
                {t('documents.acks.count', { count: d.acknowledgementCount })}
              </Button>
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="sm" onClick={() => onDownload(d)} aria-label={action(t('documents.download'), d)}><Download className="size-4" aria-hidden /></Button>
                <Button variant="ghost" size="sm" onClick={() => onEdit(d)} aria-label={action(t('common.update'), d)}><Pencil className="size-4" aria-hidden /></Button>
                <Button variant="ghost" size="sm" onClick={() => onArchive(d)} aria-label={action(t('documents.archive'), d)}><Archive className="size-4" aria-hidden /></Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export default DocumentsTable
